import { colour, space, text } from '../../design/tokens.ts';
import { Button, Card, Chip, Field, Select, Switcher, TextInput } from '../../components/basics.tsx';
import type { BoardAnswer, BoardFilter, BoardView } from './types.ts';

/**
 * Screen 3's filter panel.
 *
 * Every filter combines with every other, and none of them is applied in the
 * browser: the whole `BoardFilter` goes to the server and comes back as counts
 * plus one page of rows. That is the explicit requirement — "filters become a
 * query; do not ship 2,500 documents to the browser for the heatmap".
 *
 * The chips are the server's too (`activeChips`), so what a person sees they
 * have filtered on is exactly what the query did.
 */

type Single = 'any' | string;

/** A select that sets a one-of-many array filter, or clears it. */
function ArrayFilter<T extends string>({
  label,
  value,
  onChange,
  options
}: {
  label: string;
  value: T[] | undefined;
  onChange: (value: T[] | undefined) => void;
  options: { value: T; label: string }[];
}) {
  const current: Single = value?.length === 1 ? value[0]! : 'any';
  return (
    <Field label={label}>
      <Select<Single>
        value={current}
        onChange={(next) => onChange(next === 'any' ? undefined : ([next] as T[]))}
        options={[{ value: 'any', label: 'Any' }, ...options]}
      />
    </Field>
  );
}

export function Filters({
  filter,
  onChange,
  view,
  onView,
  answer,
  people,
  sequences,
  vcs,
  canSave,
  onSave
}: {
  filter: BoardFilter;
  onChange: (filter: BoardFilter) => void;
  view: BoardView;
  onView: (view: BoardView) => void;
  answer: BoardAnswer | null;
  people: string[];
  sequences: { _id: string; name: string }[];
  vcs: { vc_id: string; name?: string }[];
  canSave: boolean;
  onSave: () => void;
}) {
  // `undefined` means "clear this one", and the loop below drops it — so the
  // patch type has to allow it, which `Partial` under
  // exactOptionalPropertyTypes does not.
  type Patch = { [K in keyof BoardFilter]?: BoardFilter[K] | undefined };
  const set = (patch: Patch): void => {
    const next = { ...filter, ...patch };
    // An unset filter is absent rather than empty, so the server sees the same
    // shape a saved filter would hold.
    for (const [key, value] of Object.entries(next)) {
      if (value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
        delete (next as Record<string, unknown>)[key];
      }
    }
    onChange(next as BoardFilter);
  };

  const removeChip = (key: string): void => {
    const next = { ...filter };
    delete (next as Record<string, unknown>)[key];
    onChange(next);
  };

  const quick: { key: string; label: string; on: boolean; pick: () => void }[] = [
    {
      key: 'stale',
      label: 'Has a stale step',
      on: filter.staleness?.[0] === 'stale',
      pick: () => set({ staleness: filter.staleness?.[0] === 'stale' ? undefined : ['stale'] })
    },
    {
      key: 'blocked',
      label: 'Blocked',
      on: filter.state?.[0] === 'blocked',
      pick: () => set({ state: filter.state?.[0] === 'blocked' ? undefined : ['blocked'] })
    },
    {
      key: 'notmade',
      label: 'Not made yet',
      on: filter.state?.[0] === 'notmade',
      pick: () => set({ state: filter.state?.[0] === 'notmade' ? undefined : ['notmade'] })
    },
    {
      key: 'older',
      label: 'In an edit with an older movie',
      on: filter.edits?.[0] === 'older_movie',
      pick: () => set({ edits: filter.edits?.[0] === 'older_movie' ? undefined : ['older_movie'] })
    },
    {
      key: 'proxy',
      label: 'Rendered on proxy',
      on: filter.rendered_on?.[0] === 'proxy',
      pick: () => set({ rendered_on: filter.rendered_on?.[0] === 'proxy' ? undefined : ['proxy'] })
    },
    {
      key: 'accepted',
      label: 'Stale, kept as it is',
      on: filter.staleness?.[0] === 'accepted',
      pick: () => set({ staleness: filter.staleness?.[0] === 'accepted' ? undefined : ['accepted'] })
    }
  ];

  return (
    <Card>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 16 }}>
        <Switcher<BoardView>
          label="Look at it"
          value={view}
          onChange={onView}
          options={[
            { value: 'by_sequence', label: 'By sequence' },
            { value: 'by_vc', label: 'By virtual camera' }
          ]}
        />
        <Field label="Search by VC number, VC name or sequence" grow>
          <TextInput
            value={filter.search ?? ''}
            onChange={(search) => set({ search })}
            placeholder="cam0003, hero_wide, seq_010…"
          />
        </Field>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(190px, 100%), 1fr))',
          gap: space.control
        }}
      >
        <Field label="Sequence">
          <Select
            value={filter.sequence_ids?.[0] ?? 'any'}
            onChange={(id) => set({ sequence_ids: id === 'any' ? undefined : [id] })}
            options={[
              { value: 'any', label: 'Any' },
              ...sequences.map((s) => ({ value: s._id, label: s.name }))
            ]}
          />
        </Field>
        <Field label="Virtual camera">
          <Select
            value={filter.vc_ids?.[0] ?? 'any'}
            onChange={(id) => set({ vc_ids: id === 'any' ? undefined : [id] })}
            options={[
              { value: 'any', label: 'Any' },
              ...vcs.map((v) => ({ value: v.vc_id, label: v.name ? `${v.vc_id} (${v.name})` : v.vc_id }))
            ]}
          />
        </Field>
        <Field label="How far along">
          <Select
            value={filter.progress_steps?.[0] ?? (filter.include_not_started ? 'nothing' : 'any')}
            onChange={(key) =>
              set(
                key === 'any'
                  ? { progress_steps: undefined, include_not_started: undefined }
                  : key === 'nothing'
                    ? { progress_steps: undefined, include_not_started: true }
                    : { progress_steps: [key], include_not_started: undefined }
              )
            }
            options={[
              { value: 'any', label: 'Any' },
              { value: 'nothing', label: 'Nothing made yet' },
              // The steps are this project's, from the pipeline — nothing here
              // knows the name of any step.
              ...(answer?.columns ?? []).map((c) => ({ value: c.key, label: `Up to ${c.label}` }))
            ]}
          />
        </Field>
        <ArrayFilter
          label="Stale"
          value={filter.staleness}
          onChange={(staleness) => set({ staleness })}
          options={[
            { value: 'stale', label: 'Has a stale step' },
            { value: 'current', label: 'Fully current' },
            { value: 'accepted', label: 'Stale, kept as it is' }
          ]}
        />
        <ArrayFilter
          label="Edits"
          value={filter.edits}
          onChange={(edits) => set({ edits })}
          options={[
            { value: 'in_an_edit', label: 'In an edit' },
            { value: 'not_in_any_edit', label: 'Movie not in any edit' },
            { value: 'older_movie', label: 'In an edit with an older movie' }
          ]}
        />
        <Field label="Owner of any step">
          <Select
            value={filter.owner ?? 'any'}
            onChange={(owner) => set({ owner: owner === 'any' ? undefined : owner })}
            options={[{ value: 'any', label: 'Anyone' }, ...people.map((p) => ({ value: p, label: p }))]}
          />
        </Field>
        <ArrayFilter
          label="VC setup"
          value={filter.vc_setup}
          onChange={(vc_setup) => set({ vc_setup })}
          options={[
            { value: 'planned', label: 'Not created yet' },
            { value: 'stale', label: 'Setup is stale' },
            { value: 'ready', label: 'Setup is current' }
          ]}
        />
        <ArrayFilter
          label="Render made on"
          value={filter.rendered_on}
          onChange={(rendered_on) => set({ rendered_on })}
          options={[
            { value: 'proxy', label: 'Proxy splat' },
            { value: 'final', label: 'Final splat' }
          ]}
        />
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: space.tight }}>
        <span style={{ ...text.note, fontWeight: 600, color: colour.inkMuted }}>Quick filters</span>
        {quick.map((q) => (
          <Chip key={q.key} on={q.on} onClick={q.pick}>
            {q.label}
          </Chip>
        ))}
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: space.tight,
          paddingTop: space.control,
          borderTop: `1px solid ${colour.edgeSoft}`
        }}
      >
        <span style={text.bodyStrong}>
          {answer ? `${answer.matched} of ${answer.total} VC passes match` : 'Counting…'}
        </span>
        {(answer?.chips ?? []).map((chip) => (
          <Chip key={chip.key} label={chip.label} onRemove={() => removeChip(chip.key)}>
            {chip.label}
          </Chip>
        ))}
        <Button
          onClick={() => onChange({ project_id: filter.project_id, ...(filter.splat_id ? { splat_id: filter.splat_id } : {}) })}
          disabled={!answer?.filtered}
          disabledReason="Nothing is filtered"
        >
          Clear all
        </Button>
        {canSave && (
          <Button onClick={onSave} disabled={!answer?.filtered} disabledReason="Nothing is filtered">
            Save this filter
          </Button>
        )}
      </div>
    </Card>
  );
}
